import type {
  IndustryCard,
  PackageBillingDimension,
  PackageConfig,
  PackageConfigsIndex,
  PackageSelectionState,
  PricingGridsResponse,
  ProductCard,
  ProductPortfolioView,
} from "@itaas/pep-contracts";
import { productDisplayName, productDescription, productTagline } from "../utils/labels";
import { estimatePackagePrice } from "../utils/package-configurator";
import { initialQuantitiesForPackage, primarySeatCount, resolveBillingDimensions } from "../utils/billing-dimensions";

export type ConfigureIntent = "contact" | "trial";
export type ConfigureStep = 1 | 2 | 3 | 4;
export type CommercialStatus = "AVAILABLE" | "LIMITED" | "PLANNED" | "NOT_PUBLIC";

export interface ConfigureContextDefinition {
  id: "education" | "mining" | "healthcare" | "human-capital" | "finance-operations";
  packageSlug: string;
  industrySlug?: string;
  productSlug?: string;
  titleFr: string;
  titleEn: string;
  descriptionFr: string;
  descriptionEn: string;
  icon: "education" | "mining" | "healthcare" | "hr" | "operations";
}

export interface VisibleCapability {
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  selected: boolean;
  selectable: boolean;
  locked: boolean;
  commercialRole: "included" | "recommended" | "optional";
  status: CommercialStatus;
  product: ProductCard;
}

export interface PrimarySolutionCard {
  slug: string;
  title: string;
  description: string;
  tagline: string;
  features: string[];
  status: CommercialStatus;
  pricingAllowed: boolean;
  product: ProductCard;
}

export interface ConfigureDraft {
  version: string;
  step: ConfigureStep;
  contextId: ConfigureContextDefinition["id"] | null;
  packageSlug: string | null;
  selectedModules: string[];
  quantities: Record<string, number>;
  unknownQuantityKeys: string[];
  returnTo: ConfigureIntent;
}

export interface CommercialEstimateView {
  mode: "estimate" | "quote" | "missing";
  formatted: string;
  monthlyUsd?: number;
  breakdown: Array<{ label: string; amountUsd: number }>;
  note: string;
}

const PLATFORM_SLUGS = new Set(["iam", "suite", "notifications"]);

const CONTEXTS: readonly ConfigureContextDefinition[] = [
  {
    id: "education",
    packageSlug: "vertical-education",
    industrySlug: "education",
    productSlug: "edu",
    titleFr: "Éducation",
    titleEn: "Education",
    descriptionFr: "Scolarité, frais, personnel et organisation de l'établissement.",
    descriptionEn: "Academics, fees, staff, and school operations.",
    icon: "education",
  },
  {
    id: "mining",
    packageSlug: "vertical-mining",
    industrySlug: "rdc-mining",
    productSlug: "mineportal",
    titleFr: "Mines & ressources",
    titleEn: "Mining & resources",
    descriptionFr: "Sites, stock, équipes et opérations terrain.",
    descriptionEn: "Sites, inventory, teams, and field operations.",
    icon: "mining",
  },
  {
    id: "healthcare",
    packageSlug: "vertical-healthcare",
    industrySlug: "healthcare",
    productSlug: "hospitol",
    titleFr: "Santé",
    titleEn: "Healthcare",
    descriptionFr: "Pharmacy-first, stock médical et capacités hospitalières encadrées.",
    descriptionEn: "Pharmacy-first, medical inventory, and controlled hospital capabilities.",
    icon: "healthcare",
  },
  {
    id: "human-capital",
    packageSlug: "human-capital",
    productSlug: "rh",
    titleFr: "Ressources humaines",
    titleEn: "Human resources",
    descriptionFr: "Personnel, recrutement, paie et suivi RH.",
    descriptionEn: "Staff, recruitment, payroll, and HR workflows.",
    icon: "hr",
  },
  {
    id: "finance-operations",
    packageSlug: "finance-operations",
    productSlug: "erp",
    titleFr: "Commerce & opérations",
    titleEn: "Trade & operations",
    descriptionFr: "ERP, stock, facturation et opérations de gestion.",
    descriptionEn: "ERP, inventory, invoicing, and business operations.",
    icon: "operations",
  },
];

const CAPABILITY_LABELS: Record<
  string,
  { fr: { title: string; subtitle: string; description: string }; en: { title: string; subtitle: string; description: string } }
> = {
  billing: {
    fr: {
      title: "Facturation & paiements",
      subtitle: "ITaaS Invoice",
      description: "Facturation, paiements enregistrés et suivi commercial.",
    },
    en: {
      title: "Billing & payments",
      subtitle: "ITaaS Invoice",
      description: "Invoicing, payment recording, and commercial follow-up.",
    },
  },
  rh: {
    fr: {
      title: "Ressources humaines",
      subtitle: "ITaaS RH",
      description: "Personnel, congés, présence et pilotage RH.",
    },
    en: {
      title: "Human resources",
      subtitle: "ITaaS HR",
      description: "Staff, leave, attendance, and HR operations.",
    },
  },
  inventory: {
    fr: {
      title: "Stocks & fournitures",
      subtitle: "ITaaS Inventory",
      description: "Stocks, magasins, fournitures et mouvements.",
    },
    en: {
      title: "Inventory & supplies",
      subtitle: "ITaaS Inventory",
      description: "Stock, warehouses, supplies, and movements.",
    },
  },
  edu: {
    fr: {
      title: "Scolarité & pédagogie",
      subtitle: "ITaaS Edu",
      description: "Inscriptions, classes, évaluations et vie scolaire.",
    },
    en: {
      title: "School life & academics",
      subtitle: "ITaaS Edu",
      description: "Enrollment, classes, assessments, and school operations.",
    },
  },
  mineportal: {
    fr: {
      title: "Sites & opérations minières",
      subtitle: "ITaaS MinePortal",
      description: "Coordination terrain, sites et activités opérationnelles.",
    },
    en: {
      title: "Mining sites & operations",
      subtitle: "ITaaS MinePortal",
      description: "Field coordination, sites, and operational activity.",
    },
  },
  hospitol: {
    fr: {
      title: "Pharmacie & organisation de soins",
      subtitle: "ITaaS Hospitol",
      description: "Pharmacie hospitalière, stock médical et parcours encadrés.",
    },
    en: {
      title: "Pharmacy & care operations",
      subtitle: "ITaaS Hospitol",
      description: "Hospital pharmacy, medical stock, and controlled care workflows.",
    },
  },
  erp: {
    fr: {
      title: "ERP & opérations",
      subtitle: "ITaaS ERP",
      description: "Gestion opérationnelle, achats, stock et flux métiers.",
    },
    en: {
      title: "ERP & operations",
      subtitle: "ITaaS ERP",
      description: "Operational management, procurement, inventory, and workflows.",
    },
  },
  security: {
    fr: {
      title: "Sécurité privée",
      subtitle: "ITaaS Security",
      description: "Disponibilité limitée pour les opérations de gardiennage.",
    },
    en: {
      title: "Private security",
      subtitle: "ITaaS Security",
      description: "Limited availability for guarding operations.",
    },
  },
};

function roleForModule(pkg: PackageConfig, slug: string): VisibleCapability["commercialRole"] {
  if (pkg.requiredModules.includes(slug)) return "included";
  if (pkg.recommendedModules.includes(slug)) return "recommended";
  return "optional";
}

function commercialStatusForProduct(product?: ProductCard | null): CommercialStatus {
  if (!product) return "NOT_PUBLIC";
  if (product.status === "planned") return "PLANNED";
  if (product.portfolio?.publicRole === "HIDDEN") return "NOT_PUBLIC";
  if (product.portfolio?.publicRole === "LIMITED_PRODUCT") return "LIMITED";
  return "AVAILABLE";
}

function productSelectable(product?: ProductCard | null): boolean {
  const status = commercialStatusForProduct(product);
  return status === "AVAILABLE" || status === "LIMITED";
}

function publicPricingAllowed(product?: ProductCard | null): boolean {
  return product?.portfolio?.pricingAllowed === true;
}

export function configureContexts(): readonly ConfigureContextDefinition[] {
  return CONTEXTS;
}

export function visibleProductsMap(products: ProductCard[]): Map<string, ProductCard> {
  return new Map(products.map((product) => [product.slug, product]));
}

export function findPackage(index: PackageConfigsIndex, slug: string | null | undefined): PackageConfig | null {
  if (!slug) return null;
  return index.packages.find((pkg) => pkg.slug === slug) ?? null;
}

export function buildInitialCommercialSelection(
  pkg: PackageConfig,
  productsBySlug: Map<string, ProductCard>,
): PackageSelectionState {
  const selected = new Set<string>(pkg.requiredModules);
  for (const slug of pkg.recommendedModules) {
    const product = productsBySlug.get(slug);
    if (PLATFORM_SLUGS.has(slug) || productSelectable(product)) selected.add(slug);
  }

  const moduleProfiles: Record<string, string[]> = {};
  for (const [moduleId, profileIds] of Object.entries(pkg.moduleProfiles)) {
    if (selected.has(moduleId) && profileIds.length > 0) {
      moduleProfiles[moduleId] = [...profileIds];
    }
  }

  const quantities = initialQuantitiesForPackage(pkg);
  return {
    packageSlug: pkg.slug,
    selectedModules: [...selected],
    moduleProfiles,
    quantities,
    seatCount: primarySeatCount(quantities),
  };
}

export function setCommercialModuleSelected(
  state: PackageSelectionState,
  pkg: PackageConfig,
  slug: string,
  selected: boolean,
): PackageSelectionState {
  if (pkg.requiredModules.includes(slug)) return state;

  const selectedModules = selected
    ? [...new Set([...state.selectedModules, slug])]
    : state.selectedModules.filter((entry) => entry !== slug);

  const moduleProfiles = { ...state.moduleProfiles };
  if (selected) {
    if (pkg.moduleProfiles[slug]?.length) {
      moduleProfiles[slug] = [...pkg.moduleProfiles[slug]];
    }
  } else {
    delete moduleProfiles[slug];
  }

  return { ...state, selectedModules, moduleProfiles };
}

export function setCommercialQuantity(
  state: PackageSelectionState,
  key: string,
  value: number,
): PackageSelectionState {
  const quantities = { ...state.quantities, [key]: Math.max(1, value) };
  return { ...state, quantities, seatCount: primarySeatCount(quantities) };
}

export function resolveVisibleCapabilities(
  pkg: PackageConfig,
  state: PackageSelectionState,
  productsBySlug: Map<string, ProductCard>,
  locale: "fr" | "en",
): VisibleCapability[] {
  const ordered = [...pkg.requiredModules, ...pkg.recommendedModules, ...pkg.optionalModules].filter(
    (slug) => slug !== "iam",
  );
  const capabilities: VisibleCapability[] = [];

  for (const slug of ordered) {
    if (slug === pkg.primaryProduct) continue;
    const product = productsBySlug.get(slug);
    if (!product || !product.portfolio?.publicPageAllowed) continue;

    const status = commercialStatusForProduct(product);
    if (status === "NOT_PUBLIC") continue;

    const labels = CAPABILITY_LABELS[slug]?.[locale] ?? {
      title: productDisplayName(product, locale),
      subtitle: productDisplayName(product, locale),
      description: productDescription(product, locale),
    };

    capabilities.push({
      slug,
      title: labels.title,
      subtitle: labels.subtitle,
      description: labels.description,
      selected: state.selectedModules.includes(slug),
      selectable: productSelectable(product),
      locked: pkg.requiredModules.includes(slug),
      commercialRole: roleForModule(pkg, slug),
      status,
      product,
    });
  }

  return capabilities;
}

export function resolvePrimarySolution(
  pkg: PackageConfig,
  productsBySlug: Map<string, ProductCard>,
  locale: "fr" | "en",
): PrimarySolutionCard | null {
  const product = productsBySlug.get(pkg.primaryProduct);
  if (!product) return null;

  const features = (product.features ?? [])
    .filter((feature) => !/essai gratuit|30 jours/i.test(feature))
    .slice(0, 5);

  return {
    slug: product.slug,
    title: productDisplayName(product, locale),
    description: productDescription(product, locale),
    tagline: productTagline(product, locale),
    features,
    status: commercialStatusForProduct(product),
    pricingAllowed: publicPricingAllowed(product),
    product,
  };
}

export function resolveDraftFromStorage(key: string): ConfigureDraft | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConfigureDraft;
    if (!parsed?.version || typeof parsed.step !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function storeDraft(key: string, draft: ConfigureDraft) {
  localStorage.setItem(key, JSON.stringify(draft));
}

export function clearStoredDraft(key: string) {
  localStorage.removeItem(key);
}

export function resolveContextFromQuery(
  params: URLSearchParams,
  index: PackageConfigsIndex,
  industries: IndustryCard[],
): ConfigureContextDefinition | null {
  const packageSlug = params.get("package");
  if (packageSlug) {
    const byPackage = CONTEXTS.find((context) => context.packageSlug === packageSlug);
    if (byPackage && findPackage(index, byPackage.packageSlug)) return byPackage;
  }

  const product = params.get("product");
  if (product) {
    const byProduct = CONTEXTS.find((context) => context.productSlug === product);
    if (byProduct && findPackage(index, byProduct.packageSlug)) return byProduct;
  }

  const industry = params.get("industry");
  if (industry) {
    const industryExists = industries.some((entry) => entry.slug === industry);
    if (!industryExists) return null;
    const byIndustry = CONTEXTS.find((context) => context.industrySlug === industry || context.id === industry);
    if (byIndustry && findPackage(index, byIndustry.packageSlug)) return byIndustry;
  }

  return null;
}

/**
 * URL `return` mirrors commercial intent:
 * - trial → SELF_SERVICE
 * - devis | contact → ASSISTED commercial
 * Default contact when absent (legacy links); submit still derives from primary CTA mode.
 */
export function resolveReturnIntent(params: URLSearchParams): ConfigureIntent {
  const raw = params.get("return");
  if (raw === "trial") return "trial";
  if (raw === "devis" || raw === "contact") return "contact";
  return "contact";
}

export function resolveOrganizationDimensions(
  pkg: PackageConfig,
  state: PackageSelectionState,
  grids: PricingGridsResponse,
): PackageBillingDimension[] {
  return resolveBillingDimensions(pkg, state.selectedModules, grids);
}

export function commercialEstimate(
  pkg: PackageConfig,
  state: PackageSelectionState,
  index: PackageConfigsIndex,
  grids: PricingGridsResponse,
  productsBySlug: Map<string, ProductCard>,
  unknownKeys: ReadonlySet<string>,
  locale: "fr" | "en",
): CommercialEstimateView {
  const visibleSelected = state.selectedModules
    .filter((slug) => !PLATFORM_SLUGS.has(slug))
    .map((slug) => productsBySlug.get(slug))
    .filter((product): product is ProductCard => Boolean(product));

  const requiredDimensions = resolveBillingDimensions(pkg, state.selectedModules, grids);
  const missingQuantity = requiredDimensions.some(
    (dimension) => unknownKeys.has(dimension.key) || state.quantities[dimension.key] == null,
  );

  if (missingQuantity) {
    return {
      mode: "missing",
      formatted: locale === "en" ? "Estimate pending" : "Estimation en attente",
      monthlyUsd: undefined,
      breakdown: [],
      note:
        locale === "en"
          ? "Complete the organization details to unlock an indicative estimate."
          : "Complétez les informations sur votre organisation pour obtenir une estimation indicative.",
    };
  }

  const requiresQuote = visibleSelected.some((product) => !publicPricingAllowed(product));
  if (requiresQuote) {
    return {
      mode: "quote",
      formatted: locale === "en" ? "Quote on request" : "Sur devis",
      monthlyUsd: undefined,
      breakdown: [],
      note:
        locale === "en"
          ? "This configuration includes items that require commercial quoting."
          : "Cette configuration comprend des éléments qui nécessitent un chiffrage commercial.",
    };
  }

  const estimate = estimatePackagePrice(state, index, grids, locale);
  return {
    mode: "estimate",
    formatted: estimate.monthlyUsdFormatted,
    monthlyUsd: estimate.monthlyUsd,
    breakdown: estimate.breakdown,
    note:
      locale === "en"
        ? "Indicative monthly estimate based on the selected scope."
        : "Estimation mensuelle indicative selon le périmètre sélectionné.",
  };
}

export function buildCommercialSummary(args: {
  context: ConfigureContextDefinition;
  primary: PrimarySolutionCard | null;
  capabilities: VisibleCapability[];
  quantities: Record<string, number>;
  unknownKeys: ReadonlySet<string>;
  dimensions: PackageBillingDimension[];
  estimate: CommercialEstimateView;
  locale: "fr" | "en";
}): string {
  const { context, primary, capabilities, quantities, unknownKeys, dimensions, estimate, locale } = args;
  const lines: string[] = [];
  lines.push(locale === "en" ? `Context: ${context.titleEn}` : `Contexte : ${context.titleFr}`);
  if (primary) {
    lines.push(locale === "en" ? `Main solution: ${primary.title}` : `Solution principale : ${primary.title}`);
  }

  const selectedCapabilities = capabilities.filter((capability) => capability.selected);
  if (selectedCapabilities.length > 0) {
    lines.push(
      locale === "en"
        ? `Complementary capabilities: ${selectedCapabilities.map((entry) => entry.title).join(", ")}`
        : `Capacités complémentaires : ${selectedCapabilities.map((entry) => entry.title).join(", ")}`,
    );
  }

  for (const dimension of dimensions) {
    if (unknownKeys.has(dimension.key)) {
      lines.push(
        locale === "en"
          ? `${dimension.labelEn ?? dimension.labelFr}: to be confirmed`
          : `${dimension.labelFr} : à confirmer`,
      );
      continue;
    }
    lines.push(
      locale === "en"
        ? `${dimension.labelEn ?? dimension.labelFr}: ${quantities[dimension.key] ?? dimension.defaultQty}`
        : `${dimension.labelFr} : ${quantities[dimension.key] ?? dimension.defaultQty}`,
    );
  }

  lines.push(locale === "en" ? `Commercial outcome: ${estimate.formatted}` : `Résultat commercial : ${estimate.formatted}`);
  return lines.join("\n");
}

export function safeStep(step: number): ConfigureStep {
  if (step <= 1) return 1;
  if (step >= 4) return 4;
  return step as ConfigureStep;
}

export function portfolioBadgeLabel(
  status: CommercialStatus,
  locale: "fr" | "en",
): string | null {
  if (status === "LIMITED") return locale === "en" ? "Limited availability" : "Disponibilité limitée";
  if (status === "PLANNED") return locale === "en" ? "Future evolution" : "Évolution future";
  return null;
}

export function productPortfolio(product?: ProductCard | null): ProductPortfolioView | null {
  return product?.portfolio ?? null;
}
